import { Controller } from "@hotwired/stimulus"

/**
 * Toggles conversation timeline between "Oldest first" and "Newest first".
 * Persists preference in localStorage.
 *
 * data-controller="support--conversation-sort"
 * data-support--conversation-sort-storage-key-value="support_conversation_sort"
 * data-support--conversation-sort-oldest-label-value / -newest-label-value:
 *   the translated labels — the server owns the copy, never this file.
 *
 * The toggle target is a Ui::ButtonComponent toggle: "active" means newest
 * first, flipped through data-active so no design-system class lives here.
 */
export default class extends Controller {
  static targets = ["timeline", "label", "icon", "toggle"]
  static values = {
    storageKey: { type: String, default: "support_conversation_sort" },
    oldestLabel: String,
    newestLabel: String,
  }

  connect() {
    this.order = localStorage.getItem(this.storageKeyValue) || "asc"
    this.#apply()
  }

  toggle() {
    this.order = this.order === "asc" ? "desc" : "asc"
    localStorage.setItem(this.storageKeyValue, this.order)
    this.#apply()
  }

  #apply() {
    const timeline = this.timelineTarget
    const children = Array.from(timeline.children)

    if (this.order === "desc") {
      children.reverse().forEach((child) => timeline.appendChild(child))
      this.labelTarget.textContent = this.newestLabelValue
      this.iconTarget.classList.add("rotate-180")
    } else {
      // Re-sort by original DOM order (data-timestamp)
      children
        .sort((a, b) => {
          const tA = parseInt(a.dataset.timestamp || "0", 10)
          const tB = parseInt(b.dataset.timestamp || "0", 10)
          return tA - tB
        })
        .forEach((child) => timeline.appendChild(child))
      this.labelTarget.textContent = this.oldestLabelValue
      this.iconTarget.classList.remove("rotate-180")
    }

    if (this.hasToggleTarget) {
      const newestFirst = String(this.order === "desc")
      this.toggleTarget.dataset.active = newestFirst
      this.toggleTarget.setAttribute("aria-pressed", newestFirst)
    }
  }
}
